'use client';

import { c as _c } from "react/compiler-runtime";
import type { DiffNavigationContext } from '@/lib/stores/navigation/diff-navigation-store';
import { useCallback } from 'react';
import type { PrV2CommentComparison } from '../lib/pr-v2-comment-version-navigation';
import type { PrV2PendingScrollTarget, SetPrV2PendingScrollTarget } from './use-pr-v2-pending-comment-comparison';
import type { PrV2VersionOption } from './use-pr-v2-version-comparison';
import { resolveCommentTargetFilename } from '../lib/pr-v2-active-file-controller';
import { resolvePrV2CommentComparison } from '../lib/pr-v2-comment-version-navigation';
import { usePrV2PendingCommentComparison } from './use-pr-v2-pending-comment-comparison';
import { dispatchPrV2RevealComment } from '@/lib/pr-v2/reveal-comment';
type UsePrV2CommentNavigationParams = {
  canonicalBaseSha: string | null;
  canonicalHeadSha: string | null;
  currentBaseSha: string | null;
  currentHeadSha: string | null;
  comparisonKey: string | null;
  versionOptions: PrV2VersionOption[];
  versionsLoaded: boolean;
  versionsLoading: boolean;
  versionsFetching: boolean;
  versionsError: boolean;
  showComparisonLoadingPanel: boolean;
  pendingScrollTarget: PrV2PendingScrollTarget | null;
  setPendingScrollTarget: SetPrV2PendingScrollTarget;
  setComparison: (comparison: PrV2CommentComparison) => void;
  commentIdToFilename: Map<string, string>;
  selectChapterForFilename: (filename: string) => boolean;
  scrollToCommentElement: (commentId: string) => boolean;
  scrollToFileElement: (filename: string, options?: {
    applyOffset?: boolean;
  }) => boolean;
  setFileCollapsed: (filename: string, collapsed: boolean) => void;
  setManualActiveFilenameOverride: (filename: string) => void;
};
type PrV2CommentNavigationTarget = {
  commentId: string;
  filename?: string;
  targetHeadSha?: string;
};
function buildComparisonKey(baseSha: string | null, headSha: string | null): string | null {
  return baseSha && headSha ? `${baseSha}:${headSha}` : null;
}
function areShasEqual(left: string | null | undefined, right: string | null | undefined): boolean {
  return Boolean(left && right && left.toLowerCase() === right.toLowerCase());
}
function shouldWaitForVersionOptions({
  targetHeadSha,
  currentHeadSha,
  versionsLoaded,
  versionsLoading,
  versionsFetching,
  versionsError
}: {
  targetHeadSha: string | null | undefined;
  currentHeadSha: string | null;
  versionsLoaded: boolean;
  versionsLoading: boolean;
  versionsFetching: boolean;
  versionsError: boolean;
}): boolean {
  return Boolean(targetHeadSha && !areShasEqual(targetHeadSha, currentHeadSha) && !versionsLoaded && !versionsError && (versionsLoading || versionsFetching));
}
function buildPendingCommentScrollTarget({
  target,
  requiredComparisonKey,
  comparisonResolved
}: {
  target: PrV2CommentNavigationTarget;
  requiredComparisonKey: string | null;
  comparisonResolved: boolean;
}): Extract<PrV2PendingScrollTarget, {
  type: 'comment';
}> {
  return {
    type: 'comment',
    ...target,
    requiredComparisonKey,
    comparisonResolved
  };
}
function startVersionedCommentNavigation({
  target,
  targetComparison,
  waitForVersionOptions,
  setComparison,
  setFileCollapsed,
  setPendingScrollTarget
}: {
  target: PrV2CommentNavigationTarget;
  targetComparison: PrV2CommentComparison | null;
  waitForVersionOptions: boolean;
  setComparison: (comparison: PrV2CommentComparison) => void;
  setFileCollapsed: (filename: string, collapsed: boolean) => void;
  setPendingScrollTarget: SetPrV2PendingScrollTarget;
}): boolean {
  if (!targetComparison && !waitForVersionOptions) {
    return false;
  }
  if (target.filename) {
    setFileCollapsed(target.filename, false);
  }
  if (targetComparison) {
    setComparison(targetComparison);
  }
  setPendingScrollTarget(buildPendingCommentScrollTarget({
    target,
    requiredComparisonKey: targetComparison ? buildComparisonKey(targetComparison.baseSha, targetComparison.headSha) : null,
    comparisonResolved: Boolean(targetComparison)
  }));
  return true;
}
function scrollToCommentTarget({
  target,
  comparisonKey,
  selectChapterForFilename,
  scrollToCommentElement,
  scrollToFileElement,
  setFileCollapsed,
  setPendingScrollTarget
}: {
  target: PrV2CommentNavigationTarget;
  comparisonKey: string | null;
  selectChapterForFilename: (filename: string) => boolean;
  scrollToCommentElement: (commentId: string) => boolean;
  scrollToFileElement: (filename: string, options?: {
    applyOffset?: boolean;
  }) => boolean;
  setFileCollapsed: (filename: string, collapsed: boolean) => void;
  setPendingScrollTarget: SetPrV2PendingScrollTarget;
}): boolean {
  const didSwitchChapter = target.filename ? selectChapterForFilename(target.filename) : false;
  if (!didSwitchChapter && scrollToCommentElement(target.commentId)) {
    return true;
  }
  if (target.filename) {
    setFileCollapsed(target.filename, false);
    if (!didSwitchChapter && scrollToFileElement(target.filename, {
      applyOffset: false
    })) {
      setPendingScrollTarget(buildPendingCommentScrollTarget({
        target,
        requiredComparisonKey: comparisonKey,
        comparisonResolved: true
      }));
      return true;
    }
  }
  setPendingScrollTarget(buildPendingCommentScrollTarget({
    target,
    requiredComparisonKey: comparisonKey,
    comparisonResolved: true
  }));
  return true;
}
export function usePrV2CommentNavigation(params) {
  const $ = _c(22);
  const {
    canonicalBaseSha,
    canonicalHeadSha,
    currentBaseSha,
    currentHeadSha,
    comparisonKey,
    versionOptions,
    versionsLoaded,
    versionsLoading,
    versionsFetching,
    versionsError,
    showComparisonLoadingPanel,
    pendingScrollTarget,
    setPendingScrollTarget,
    setComparison,
    commentIdToFilename,
    selectChapterForFilename,
    scrollToCommentElement,
    scrollToFileElement,
    setFileCollapsed,
    setManualActiveFilenameOverride
  } = params;
  usePrV2PendingCommentComparison(params);
  let t0;
  if ($[0] !== canonicalBaseSha || $[1] !== canonicalHeadSha || $[2] !== commentIdToFilename || $[3] !== comparisonKey || $[4] !== currentBaseSha || $[5] !== currentHeadSha || $[6] !== scrollToCommentElement || $[7] !== scrollToFileElement || $[8] !== selectChapterForFilename || $[9] !== setComparison || $[10] !== setFileCollapsed || $[11] !== setManualActiveFilenameOverride || $[12] !== setPendingScrollTarget || $[13] !== versionOptions || $[14] !== versionsError || $[15] !== versionsFetching || $[16] !== versionsLoaded || $[17] !== versionsLoading) {
    t0 = (commentId, context) => {
      if (!commentId) {
        return false;
      }
      setPendingScrollTarget(null);
      dispatchPrV2RevealComment(commentId);
      const filename = resolveCommentTargetFilename(commentId, context?.filename, commentIdToFilename);
      if (filename) {
        setManualActiveFilenameOverride(filename);
      }
      const target = {
        commentId,
        filename: filename ?? undefined,
        targetHeadSha: context?.targetHeadSha
      };
      const targetComparison = resolvePrV2CommentComparison({
        canonicalBaseSha,
        canonicalHeadSha,
        currentBaseSha,
        currentHeadSha,
        targetHeadSha: context?.targetHeadSha ?? null,
        versionOptions
      });
      const waitForVersionOptions = shouldWaitForVersionOptions({
        targetHeadSha: context?.targetHeadSha,
        currentHeadSha,
        versionsLoaded,
        versionsLoading,
        versionsFetching,
        versionsError
      });
      if (startVersionedCommentNavigation({
        target,
        targetComparison,
        waitForVersionOptions,
        setComparison,
        setFileCollapsed,
        setPendingScrollTarget
      })) {
        return true;
      }
      return scrollToCommentTarget({
        target,
        comparisonKey,
        selectChapterForFilename,
        scrollToCommentElement,
        scrollToFileElement,
        setFileCollapsed,
        setPendingScrollTarget
      });
    };
    $[0] = canonicalBaseSha;
    $[1] = canonicalHeadSha;
    $[2] = commentIdToFilename;
    $[3] = comparisonKey;
    $[4] = currentBaseSha;
    $[5] = currentHeadSha;
    $[6] = scrollToCommentElement;
    $[7] = scrollToFileElement;
    $[8] = selectChapterForFilename;
    $[9] = setComparison;
    $[10] = setFileCollapsed;
    $[11] = setManualActiveFilenameOverride;
    $[12] = setPendingScrollTarget;
    $[13] = versionOptions;
    $[14] = versionsError;
    $[15] = versionsFetching;
    $[16] = versionsLoaded;
    $[17] = versionsLoading;
    $[18] = t0;
  } else {
    t0 = $[18];
  }
  const navigateToComment = t0;
  const isPendingScrollTargetReady = pendingScrollTarget?.type !== "comment" || pendingScrollTarget.comparisonResolved && pendingScrollTarget.requiredComparisonKey === comparisonKey && !showComparisonLoadingPanel;
  let t1;
  if ($[19] !== isPendingScrollTargetReady || $[20] !== navigateToComment) {
    t1 = {
      navigateToComment,
      isPendingScrollTargetReady
    };
    $[19] = isPendingScrollTargetReady;
    $[20] = navigateToComment;
    $[21] = t1;
  } else {
    t1 = $[21];
  }
  return t1;
}